import { transparentize } from 'polished';
import { useTheme } from 'styled-components';
import { Box, HStack } from '../../../Core';
import { Icon } from '../../../Icons/Icon';
import { IconName } from '../../../Icons/IconNames';
import { Text } from '../../../Text';

const STEP_ICON_DIAMETER = 36;
const STEP_ICON_SIZE = 16;

/** One step in an EntityAdmin multi-step workflow header (icon + label). */
export interface EntityAdminWorkflowStep<TStepId extends string = string> {
  id: TStepId;
  label: string;
  icon: IconName;
}

export interface EntityAdminWorkflowStepperProps<TStepId extends string = string> {
  steps: readonly EntityAdminWorkflowStep<TStepId>[];
  currentStepId: string;
  onSelectStep?: (stepId: TStepId) => void;
}

/**
 * Horizontal stepper with icons and a connector line between steps.
 * Used by EntityAdmin workflows opened in the app-wide drawer.
 */
export function EntityAdminWorkflowStepper<TStepId extends string>({
  steps,
  currentStepId,
  onSelectStep,
}: EntityAdminWorkflowStepperProps<TStepId>) {
  const theme = useTheme();
  const selectedIndex = steps.findIndex(step => step.id === currentStepId);
  const connectorOffset = steps.length > 1 ? `calc(${50 / steps.length}% + ${STEP_ICON_DIAMETER / 2}px)` : null;

  const tileBackground = transparentize(0.96, theme.colors.gray['100']);
  const tileBorder = transparentize(0.92, theme.colors.gray['100']);
  const completedTileBackground = transparentize(0.76, theme.colors.green.default);

  return (
    <Box display="flex" justifyContent="center" py="spacing12" px="spacing16">
      <Box position="relative" w="100%">
        {connectorOffset != null && (
          <Box
            position="absolute"
            top={`${STEP_ICON_DIAMETER / 2}px`}
            left={connectorOffset}
            right={connectorOffset}
            h="1px"
            background={selectedIndex > 0 ? 'colors.gray.070' : 'colors.gray.030'}
          />
        )}
        <HStack alignItems="flex-start" justifyContent="space-between" gap="spacing24">
          {steps.map((step, index) => {
            const isActive = index === selectedIndex;
            const isComplete = index < selectedIndex;
            const canNavigateToStep = index <= selectedIndex;

            const tileBg = isComplete ? completedTileBackground : tileBackground;
            const tileBorderColor = isComplete ? completedTileBackground : tileBorder;
            const iconColor = isComplete
              ? theme.colors.green.lighten
              : isActive
                ? theme.colors.gray['100']
                : theme.colors.gray['060'];

            return (
              <Box
                key={step.id}
                flex={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
                cursor={onSelectStep && canNavigateToStep ? 'pointer' : 'default'}
                opacity={canNavigateToStep ? 1 : 0.45}
                onClick={() => {
                  if (onSelectStep && canNavigateToStep) {
                    onSelectStep(step.id);
                  }
                }}
              >
                <Box
                  w={`${STEP_ICON_DIAMETER}px`}
                  h={`${STEP_ICON_DIAMETER}px`}
                  mb="spacing6"
                  position="relative"
                  zIndex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="100%"
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor={tileBorderColor}
                  background={tileBg}
                  color={iconColor}
                >
                  <Icon icon={isComplete ? IconName.Check : step.icon} size={STEP_ICON_SIZE} />
                </Box>
                <Text
                  color={isActive ? 'colors.gray.100' : 'colors.gray.070'}
                  whiteSpace="nowrap"
                  textAlign="center"
                  fontWeight="400"
                  fontSize="12px"
                  lineHeight="16px"
                >
                  {step.label}
                </Text>
              </Box>
            );
          })}
        </HStack>
      </Box>
    </Box>
  );
}
